import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  EmptyState,
  Input,
  Label,
  MoneyInput,
  PageHeader,
  Select,
  SkeletonRows,
  toast,
} from '@bia-auction/ui';
import { t } from '@bia-auction/shared-i18n';
import type {
  EligibleVehicle,
  SaleCreateInput,
  SaleDraft,
  SaleStyle,
} from '@bia-auction/shared-schema';
import {
  eligibleVehicleSchema,
  saleDetailSchema,
  saleDraftResponseSchema,
} from '@bia-auction/shared-schema';
import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useNavigate } from '@tanstack/react-router';
import { AlertCircle, Car, Check } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { CompanyPicker } from '../components/CompanyPicker';
import { VehicleThumb } from '../components/VehicleThumb';
import { ErrorMsg } from '../components/Feedback';
import { api, ApiError } from '../lib/api';
import { formatKurus, formatTime } from '../lib/format';
import { SALE_STYLES, saleStyleLabel } from '../lib/labels';

// Approximate per-lot active minutes (mirrors the server's schedule for a client-only preview).
const STYLE_MINUTES: Record<SaleStyle, number> = {
  parallel: 30,
  serial: 2,
  'serial-10': 10,
  'serial-with-cooldown': 3,
};

/** epoch-ms → value for a `datetime-local` input (local time). Empty for an invalid instant. */
function toLocalInput(ms: number): string {
  if (!Number.isFinite(ms)) return '';
  // Use the TARGET instant's own UTC offset, not today's — across a DST boundary the two differ,
  // and using today's would shift the displayed (and then re-submitted) time by an hour.
  const d = new Date(ms);
  return new Date(ms - d.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}
/** `datetime-local` value → epoch-ms, or NaN when the field is cleared/invalid. */
function fromLocalInput(v: string): number {
  return new Date(v).getTime();
}

/** Per-selected-vehicle lot config (kuruş). */
interface LotConfig {
  startingPrice: number;
  reserve: number | null;
  guaranteed: boolean;
}

type DraftState = { kind: 'idle' } | { kind: 'saved'; at: number } | { kind: 'failed' };

export function NewSalePage() {
  const navigate = useNavigate();
  const inOneHour = Date.now() + 3_600_000;

  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [style, setStyle] = useState<SaleStyle>('serial');
  const [jointConference, setJointConference] = useState(false);
  const [listingDate, setListingDate] = useState(inOneHour);
  const [startDate, setStartDate] = useState(inOneHour + 3_600_000);
  const [defaultReserve, setDefaultReserve] = useState<number | null>(null); // kuruş
  const [targetCompany, setTargetCompany] = useState<string[]>([]);
  const [selected, setSelected] = useState<Record<string, LotConfig>>({});
  const [vehicleFilter, setVehicleFilter] = useState('');
  const [draftState, setDraftState] = useState<DraftState>({ kind: 'idle' });
  // Reactive (not a ref): flipping it on GET-settle must re-render so the autosave effect
  // re-runs — otherwise edits typed *during* a slow draft load, which hit the guard while
  // still false, would never be scheduled once hydration completes.
  const [hydrated, setHydrated] = useState(false);
  const autosaveAbort = useRef<AbortController | null>(null);
  const submitted = useRef(false); // once submitting, pending autosaves must not re-write the draft

  const vehicles = useQuery({
    queryKey: ['vehicles', 'eligible'],
    queryFn: () => api('/admin/vehicles/eligible', {}, z.array(eligibleVehicleSchema)),
  });

  // Load any saved draft once.
  useEffect(() => {
    void (async () => {
      const draft = await api('/admin/sales/draft', {}, saleDraftResponseSchema);
      if (draft) {
        if (draft.title) setTitle(draft.title);
        if (draft.location) setLocation(draft.location);
        if (draft.style) setStyle(draft.style);
        if (draft.jointConference !== undefined) setJointConference(draft.jointConference);
        if (draft.listingDate) setListingDate(draft.listingDate);
        if (draft.startDate) setStartDate(draft.startDate);
        if (draft.defaultReserve) setDefaultReserve(draft.defaultReserve);
        if (draft.targetCompany) setTargetCompany(draft.targetCompany);
        if (draft.lots) {
          const sel: Record<string, LotConfig> = {};
          for (const l of draft.lots) {
            if (l.vehicleId)
              sel[l.vehicleId] = {
                startingPrice: l.startingPrice ?? 0,
                reserve: l.reserve ?? null,
                guaranteed: l.saleMode === 'guaranteed',
              };
          }
          setSelected(sel);
        }
      }
      // Flip AFTER applying the draft (so the re-triggered autosave sees hydrated values, not
      // defaults), and regardless of whether a draft existed — a first-time account editing only
      // the header should still autosave. This state change re-renders, which re-runs the
      // autosave effect and picks up any edits typed while the GET was in flight.
      setHydrated(true);
    })();
  }, []);

  // Reconcile hydrated selections against the eligible pool. A vehicle saved in the draft may
  // have been scheduled/sold by another operator since — `/admin/vehicles/eligible` then omits
  // it, so the picker can't render or deselect it, yet create/autosave keep sending it and every
  // create 409s with no UI repair path. Drop any no-longer-eligible id (the autosave persists the
  // repaired draft); returning the same object ref when nothing changed avoids a render loop.
  useEffect(() => {
    if (!vehicles.data) return;
    const eligible = new Set(vehicles.data.map((v) => v.id));
    setSelected((s) => {
      const kept = Object.fromEntries(Object.entries(s).filter(([id]) => eligible.has(id)));
      const dropped = Object.keys(s).length - Object.keys(kept).length;
      if (dropped === 0) return s;
      toast(t('newSale.draftVehiclesDropped', { count: String(dropped) }), 'error');
      return kept;
    });
  }, [vehicles.data]);

  // Debounced draft autosave (includes defaultReserve + targetCompany).
  useEffect(() => {
    // Never autosave before the initial GET resolves — otherwise a title/vehicle typed while a
    // slow draft load is in flight would PUT the partial form defaults over the saved remote
    // draft, and the late GET could then re-hydrate that just-lost data. `hydrated` (reactive)
    // flips true once the GET settles, re-running this effect so in-flight edits are saved.
    if (!hydrated) return;
    // A new edit invalidates the last outcome — don't keep showing "saved" (or a stale
    // failure) while unsaved changes sit in the debounce window.
    setDraftState((s) => (s.kind === 'idle' ? s : { kind: 'idle' }));
    const timer = setTimeout(() => {
      if (submitted.current) return; // don't resurrect the draft after submit
      autosaveAbort.current?.abort();
      autosaveAbort.current = new AbortController();
      const draft: SaleDraft = {
        title,
        location,
        style,
        jointConference,
        listingDate,
        startDate,
        ...(defaultReserve !== null ? { defaultReserve } : {}),
        targetCompany,
        lots: Object.entries(selected).map(([vehicleId, cfg]) => ({
          vehicleId,
          startingPrice: cfg.startingPrice,
          ...(cfg.reserve !== null ? { reserve: cfg.reserve } : {}),
          saleMode: cfg.guaranteed ? ('guaranteed' as const) : ('normal' as const),
        })),
      };
      void api('/admin/sales/draft', {
        method: 'PUT',
        body: draft,
        signal: autosaveAbort.current.signal,
      })
        // Surface the save state in the header (redesign-admin.md §4) — the silent autosave
        // left operators unsure whether closing the tab would lose the form.
        .then(() => setDraftState({ kind: 'saved', at: Date.now() }))
        .catch((e: unknown) => {
          if (e instanceof DOMException && e.name === 'AbortError') return; // superseded save
          setDraftState({ kind: 'failed' });
        });
    }, 1_200);
    return () => clearTimeout(timer);
  }, [hydrated, title, location, style, jointConference, listingDate, startDate, defaultReserve, targetCompany, selected]);

  const create = useMutation({
    mutationFn: (body: SaleCreateInput) =>
      api('/admin/sales', { method: 'POST', body }, saleDetailSchema),
    onSuccess: async (sale) => {
      // Cancel any in-flight/pending autosave first so it can't rewrite the draft AFTER we
      // clear it, then clear the saved draft — otherwise the just-published vehicles (now
      // excluded from /admin/vehicles/eligible) would be rehydrated as invisible,
      // un-deselectable selections that fail the next create on vehicle-uniqueness.
      autosaveAbort.current?.abort();
      const cleared = await api('/admin/sales/draft', { method: 'PUT', body: {} })
        .then(() => true)
        .catch(() => false);
      // Don't report plain success if the draft couldn't be cleared — surface it so the
      // operator knows the stale draft still holds the just-published vehicles.
      toast(cleared ? t('newSale.created') : t('newSale.draftClearFailed'), cleared ? 'success' : 'error');
      void navigate({ to: '/sales/$saleId', params: { saleId: sale.id } });
    },
    onError: (e) => {
      // Creation failed — re-enable autosave so the operator's later corrections are saved again.
      submitted.current = false;
      toast(e instanceof ApiError ? e.message : t('newSale.createFailed'), 'error');
    },
  });

  const lotCount = Object.keys(selected).length;
  const previewMinutes =
    style === 'parallel' ? STYLE_MINUTES.parallel : STYLE_MINUTES[style] * lotCount;

  const toggleVehicle = (v: EligibleVehicle) =>
    setSelected((s) => {
      const next = { ...s };
      if (v.id in next) delete next[v.id];
      else next[v.id] = { startingPrice: v.auctionPrice ?? 0, reserve: v.auctionReserve, guaranteed: false };
      return next;
    });
  const patchLot = (id: string, patch: Partial<LotConfig>) =>
    setSelected((s) => ({ ...s, [id]: { ...s[id]!, ...patch } }));

  const submit = () => {
    if (lotCount === 0) return toast(t('newSale.needVehicle'), 'error');
    submitted.current = true; // freeze autosave — a pending debounce must not re-save the draft
    const companies = targetCompany;
    const body: SaleCreateInput = {
      title: title || undefined,
      location: location || undefined,
      style,
      jointConference,
      listingDate,
      startDate,
      ...(defaultReserve !== null ? { defaultReserve } : {}),
      targetCompany: companies.length > 0 ? companies : undefined,
      lots: Object.entries(selected).map(([vehicleId, cfg], i) => ({
        vehicleId,
        orderIndex: i,
        startingPrice: cfg.startingPrice,
        ...(cfg.reserve !== null ? { reserve: cfg.reserve } : {}),
        ...(cfg.guaranteed ? { saleMode: 'guaranteed' as const } : {}),
      })),
    };
    create.mutate(body);
  };

  const filter = vehicleFilter.trim().toLocaleLowerCase('tr');
  const visibleVehicles = (vehicles.data ?? []).filter(
    (v) =>
      !filter ||
      v.plate.toLocaleLowerCase('tr').includes(filter) ||
      [v.brand, v.series, v.modelName].filter(Boolean).join(' ').toLocaleLowerCase('tr').includes(filter),
  );

  return (
    <div className="space-y-4 pb-16">
      <PageHeader title={t('newSale.title')}>
        {/* Autosave indicator (redesign-admin.md §4) — icon + text, never color alone. */}
        {draftState.kind === 'saved' && (
          <span className="flex items-center gap-1 text-sm text-muted-foreground">
            <Check aria-hidden className="h-4 w-4" />
            {t('newSale.draftSaved', { time: formatTime(draftState.at) })}
          </span>
        )}
        {draftState.kind === 'failed' && (
          <span className="flex items-center gap-1 rounded-md bg-danger-tint px-2 py-1 text-sm text-danger-tint-fg">
            <AlertCircle aria-hidden className="h-4 w-4" />
            {t('newSale.draftFailed')}
          </span>
        )}
      </PageHeader>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>{t('newSale.general')}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="ns-title">{t('newSale.titleField')}</Label>
              <Input id="ns-title" value={title} onChange={(e) => setTitle(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ns-location">{t('newSale.location')}</Label>
              <Input id="ns-location" value={location} onChange={(e) => setLocation(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ns-style">{t('newSale.style')}</Label>
              <Select id="ns-style" value={style} onChange={(e) => setStyle(e.target.value as SaleStyle)}>
                {SALE_STYLES.map((s) => (
                  <option key={s} value={s}>
                    {saleStyleLabel(s)}
                  </option>
                ))}
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ns-reserve">{t('newSale.defaultReserve')}</Label>
              <MoneyInput id="ns-reserve" value={defaultReserve} onValueChange={setDefaultReserve} />
            </div>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox
                checked={jointConference}
                onCheckedChange={(v) => setJointConference(v === true)}
              />
              {t('newSale.joint')}
            </label>
            <div />
            <div className="space-y-1.5">
              <Label htmlFor="ns-listing">{t('newSale.listing')}</Label>
              <Input
                id="ns-listing"
                type="datetime-local"
                value={toLocalInput(listingDate)}
                onChange={(e) => {
                  const v = fromLocalInput(e.target.value);
                  if (Number.isFinite(v)) setListingDate(v);
                }}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ns-start">{t('newSale.start')}</Label>
              <Input
                id="ns-start"
                type="datetime-local"
                value={toLocalInput(startDate)}
                onChange={(e) => {
                  const v = fromLocalInput(e.target.value);
                  if (Number.isFinite(v)) setStartDate(v);
                }}
              />
            </div>
            <div className="col-span-2 space-y-1.5">
              <Label>{t('newSale.targetCompanies')}</Label>
              <CompanyPicker value={targetCompany} onChange={setTargetCompany} />
              {/* Empty selection = public sale (no firm targeting). */}
              <div className="text-xs text-muted-foreground">{t('newSale.targetPlaceholder')}</div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('newSale.vehicles', { n: lotCount, min: previewMinutes })}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <Input
              placeholder={t('newSale.vehicleFilter')}
              value={vehicleFilter}
              onChange={(e) => setVehicleFilter(e.target.value)}
            />
            {vehicles.isLoading ? (
              <SkeletonRows rows={5} />
            ) : vehicles.isError ? (
              <ErrorMsg error={vehicles.error} />
            ) : (vehicles.data?.length ?? 0) === 0 ? (
              <EmptyState icon={Car} title={t('newSale.noVehicles')} />
            ) : (
              <div className="max-h-[28rem] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-surface">
                    <tr className="border-b text-left text-muted-foreground">
                      <th className="w-8 p-2" />
                      <th className="p-2 font-medium">{t('newSale.col.plate')}</th>
                      <th className="p-2 font-medium">{t('newSale.col.vehicle')}</th>
                      <th className="p-2 font-medium">{t('newSale.col.start')}</th>
                      <th className="p-2 font-medium">{t('newSale.col.reserve')}</th>
                      <th className="p-2 font-medium">{t('newSale.guaranteed')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleVehicles.map((v) => {
                      const cfg = selected[v.id];
                      return (
                        <tr key={v.id} className="border-b align-middle hover:bg-secondary">
                          <td className="p-2">
                            <Checkbox
                              aria-label={v.plate}
                              checked={!!cfg}
                              onCheckedChange={() => toggleVehicle(v)}
                            />
                          </td>
                          <td className="p-2 font-mono">{v.plate}</td>
                          <td className="p-2 text-muted-foreground">
                            {/* M11: a thumbnail + `Brand Model · Year` — the picker used to
                                list plates, which is not how anyone recognises a car. */}
                            <div className="flex items-center gap-2">
                              <VehicleThumb url={v.coverUrl} plate={v.plate} className="h-9 w-12" />
                              <span>
                                {[v.brand, v.series, v.modelName].filter(Boolean).join(' ')}
                                {v.year ? ` · ${v.year}` : ''}
                              </span>
                            </div>
                          </td>
                          <td className="p-2 tabular-nums">
                            {cfg ? (
                              <MoneyInput
                                aria-label={t('newSale.col.start')}
                                className="w-32"
                                value={cfg.startingPrice}
                                onValueChange={(k) => patchLot(v.id, { startingPrice: k ?? 0 })}
                              />
                            ) : (
                              formatKurus(v.auctionPrice)
                            )}
                          </td>
                          <td className="p-2">
                            {cfg && (
                              <MoneyInput
                                aria-label={t('newSale.col.reserve')}
                                className="w-32"
                                placeholder={t('newSale.reservePlaceholder')}
                                value={cfg.reserve}
                                disabled={cfg.guaranteed}
                                onValueChange={(k) => patchLot(v.id, { reserve: k })}
                              />
                            )}
                          </td>
                          <td className="p-2">
                            {cfg && (
                              <Checkbox
                                aria-label={t('newSale.guaranteed')}
                                checked={cfg.guaranteed}
                                // A guaranteed lot has no reserve semantics — clear it on check so
                                // the draft/create never carry both (the disabled input alone kept
                                // the stale value in state).
                                onCheckedChange={(val) =>
                                  patchLot(
                                    v.id,
                                    val === true
                                      ? { guaranteed: true, reserve: null }
                                      : { guaranteed: false },
                                  )
                                }
                              />
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {visibleVehicles.length === 0 && (
                      <tr>
                        <td colSpan={6} className="p-4 text-center text-muted-foreground">
                          {t('newSale.noVehicles')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Sticky action bar (redesign-admin.md §4) — the one filled button of the view. */}
      <div className="sticky bottom-0 -mx-8 flex justify-end gap-2 border-t bg-surface px-8 py-3">
        <Button variant="outline" onClick={() => navigate({ to: '/sales' })}>
          {t('common.cancel')}
        </Button>
        <Button onClick={submit} disabled={create.isPending}>
          {t('newSale.submit')}
        </Button>
      </div>
    </div>
  );
}
